/**
 * @file     HomeValueSection.tsx
 * @purpose  "Home value & rental outlook" card — the SigmaEstimate / rent / yield
 *           / DOM estimate strip plus School / Growth / Rental star scores.
 *           This is members-only content ("Sign-in required" in the source UI),
 *           so the whole card's VALUES are gated. Recommended placement: a
 *           standalone card directly under "About this home" — it's the strongest
 *           sign-in hook — above Listing history / Market demand. If you prefer,
 *           wrap in <PremiumPanelGate panel="home_value"> like the other bands;
 *           otherwise the built-in blur + Member tag handles the gate.
 *
 */
'use client';

import { layoutTokens } from '@/design-system/tokens/layout';
import * as React from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import { useAppTheme } from '@/core/theme/useAppTheme';
import { useReveal } from './useReveal';

const ACCENT = '#4DB2BA';
/** Glyphs and text: ACCENT is 2.5:1 on a light surface; the strong accent clears 3:1 in both themes. */
const ACCENT_INK = 'var(--r-accent-strong)';
const EASE = 'cubic-bezier(.22,.61,.36,1)';
const POP = 'cubic-bezier(.34,1.56,.64,1)';

const STAT_ICON: Record<string, string> = {
  estimate: 'M21.41 11.58l-9-9C12.05 2.22 11.55 2 11 2H4c-1.1 0-2 .9-2 2v7c0 .55.22 1.05.59 1.42l9 9c.36.36.86.58 1.41.58s1.05-.22 1.42-.59l7-7c.36-.36.58-.86.58-1.41s-.23-1.06-.59-1.42zM5.5 7C4.67 7 4 6.33 4 5.5S4.67 4 5.5 4 7 4.67 7 5.5 6.33 7 5.5 7z',
  date: 'M19 4h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V10h14v10zm0-12H5V6h14v2z',
  rent: 'M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z',
  yield: 'M7.5 11C9.43 11 11 9.43 11 7.5S9.43 4 7.5 4 4 5.57 4 7.5 5.57 11 7.5 11zm0-5C8.33 6 9 6.67 9 7.5S8.33 9 7.5 9 6 8.33 6 7.5 6.67 6 7.5 6zM4.21 18.38 18.38 4.21 19.79 5.62 5.62 19.79zM16.5 13c-1.93 0-3.5 1.57-3.5 3.5s1.57 3.5 3.5 3.5 3.5-1.57 3.5-3.5-1.57-3.5-3.5-3.5z',
  dom: 'M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z',
};

const STAR = 'M12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z';

export interface HomeStat {
  iconKey: keyof typeof STAT_ICON;
  label: string;
  value: string;
}
export interface ScoreRow {
  label: string;
  value: number; // 0..5, halves OK
}

function Star({ kind }: { kind: 'full' | 'half' | 'empty' }) {
  const { mode } = useAppTheme();
  const empty = mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.14)';
  if (kind === 'half') {
    return (
      <Box component="span" sx={{ position: 'relative', display: 'inline-block', width: 17, height: 17 }}>
        <Box component="svg" viewBox="0 0 24 24" sx={{ width: 17, height: 17, position: 'absolute', inset: 0 }} fill={empty}><path d={STAR} /></Box>
        <Box component="svg" viewBox="0 0 24 24" sx={{ width: 17, height: 17, position: 'absolute', inset: 0, clipPath: 'inset(0 50% 0 0)' }} fill={ACCENT_INK}><path d={STAR} /></Box>
      </Box>
    );
  }
  return <Box component="svg" viewBox="0 0 24 24" sx={{ width: 17, height: 17 }} fill={kind === 'full' ? ACCENT_INK : empty}><path d={STAR} /></Box>;
}

function Stars({ value, lit }: { value: number; lit: boolean }) {
  const full = Math.floor(value);
  const half = value - full >= 0.5;
  const cells: Array<'full' | 'half' | 'empty'> = [];
  for (let i = 0; i < full; i++) cells.push('full');
  if (half) cells.push('half');
  while (cells.length < 5) cells.push('empty');
  return (
    <Stack direction="row" spacing={0.4}>
      {cells.map((k, i) => (
        <Box
          key={i}
          component="span"
          sx={{
            display: 'inline-flex',
            transform: lit ? 'scale(1)' : 'scale(.3)',
            opacity: lit ? 1 : 0,
            transition: `transform .45s ${POP}, opacity .3s ease`,
            transitionDelay: `${i * 55}ms`,
            willChange: 'transform',
          }}
        >
          <Star kind={k} />
        </Box>
      ))}
    </Stack>
  );
}

function GatedValue({ gated, children }: { gated: boolean; children: React.ReactNode }) {
  return (
    <Box component="span" aria-hidden={gated || undefined} sx={{ filter: gated ? 'blur(5px)' : 'none', transition: 'filter .35s', userSelect: gated ? 'none' : undefined }}>
      {children}
    </Box>
  );
}

interface Props {
  subtitle: string; // "Estimated valuation for <addr> · listed at $X"
  stats: HomeStat[];
  scores: ScoreRow[];
  isAuthenticated: boolean;
}

export function HomeValueSection({ subtitle, stats, scores, isAuthenticated }: Props) {
  const { mode } = useAppTheme();
  const isDark = mode === 'dark';
  const gated = !isAuthenticated;
  const reveal = useReveal();
  const lit = reveal.shown && !reveal.reduce;
  const divider = `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)'}`;

  return (
    <Box ref={reveal.ref} sx={{ bgcolor: 'background.paper', border: `1px solid ${isDark ? 'rgba(255,255,255,0.09)' : 'rgba(0,0,0,0.08)'}`, borderRadius: `${layoutTokens.radius.lg}px`, boxShadow: isDark ? '0 18px 50px rgba(0,0,0,0.5)' : '0 14px 40px rgba(0,0,0,0.07)', p: { xs: '24px 22px', md: '30px 34px' }, opacity: reveal.shown ? 1 : 0, transform: reveal.shown ? 'none' : 'translateY(18px)', transition: reveal.reduce ? 'none' : `opacity .55s ${EASE}, transform .55s ${EASE}`, willChange: 'opacity, transform' }}>
      <Stack direction="row" alignItems="center" spacing={1.6} flexWrap="wrap" sx={{ mb: 1 }}>
        <Box sx={{ width: 36, height: 36, borderRadius: `${layoutTokens.radius.md}px`, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: alpha(ACCENT, 0.08), border: `1px solid ${alpha(ACCENT, 0.16)}`, color: ACCENT_INK, flexShrink: 0 }}>
          <Box component="svg" viewBox="0 0 24 24" sx={{ width: 19, height: 19 }} fill="currentColor"><path d={STAT_ICON.estimate} /></Box>
        </Box>
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Typography sx={{ fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: ACCENT_INK }}>Estimate</Typography>
          <Typography component="h3" sx={{ fontSize: '1.1875rem', fontWeight: 600, letterSpacing: '-0.01em' }}>Home value &amp; rental outlook</Typography>
        </Box>
        {gated && (
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: '0.625rem', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: ACCENT_INK, bgcolor: alpha(ACCENT, isDark ? 0.12 : 0.09), border: `1px solid ${alpha(ACCENT, 0.2)}`, px: 0.9, py: 0.25, borderRadius: `${layoutTokens.radius.pill}px` }}>
            <Box component="svg" viewBox="0 0 24 24" sx={{ width: 9, height: 9 }} fill="currentColor"><path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM9 6c0-1.66 1.34-3 3-3s3 1.34 3 3v2H9V6z" /></Box>
            Member
          </Box>
        )}
      </Stack>
      <Typography sx={{ fontSize: '0.84rem', color: 'text.secondary', mb: 3 }}>{subtitle}</Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '20px 10px', pb: 3, borderBottom: divider }}>
        {stats.map((s, i) => (
          <Stack
            key={s.label}
            alignItems="center"
            spacing={1}
            sx={{
              textAlign: 'center',
              opacity: reveal.shown ? 1 : 0,
              transform: reveal.shown ? 'none' : 'translateY(10px)',
              transition: reveal.reduce ? 'none' : `opacity .5s ${EASE}, transform .5s ${EASE}`,
              transitionDelay: reveal.reduce ? undefined : `${120 + i * 70}ms`,
              '&:hover': reveal.reduce ? undefined : { transform: 'translateY(-3px)' },
            }}
          >
            <Box component="svg" viewBox="0 0 24 24" sx={{ width: 27, height: 27 }} fill={ACCENT_INK}><path d={STAT_ICON[s.iconKey]} /></Box>
            <Stack direction="row" alignItems="center" spacing={0.6}>
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 500, color: 'text.secondary' }}>{s.label}</Typography>
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 14, height: 14, borderRadius: `${layoutTokens.radius.pill}px`, border: `1px solid ${isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.18)'}`, fontSize: '0.5625rem', fontWeight: 600, color: 'text.disabled' }}>?</Box>
            </Stack>
            <Typography sx={{ fontSize: '1rem', fontWeight: 600 }}><GatedValue gated={gated}>{s.value}</GatedValue></Typography>
          </Stack>
        ))}
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '16px 24px', mt: 3 }}>
        {scores.map((sc) => (
          <Stack key={sc.label} direction="row" alignItems="center" spacing={1.1}>
            <Typography sx={{ fontSize: '0.84rem', fontWeight: 500 }}>{sc.label}</Typography>
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 15, height: 15, borderRadius: `${layoutTokens.radius.pill}px`, border: `1px solid ${alpha(ACCENT, 0.4)}`, fontSize: '0.5625rem', fontWeight: 600, color: ACCENT_INK, fontStyle: 'italic' }}>i</Box>
            <GatedValue gated={gated}><Stars value={sc.value} lit={lit} /></GatedValue>
          </Stack>
        ))}
      </Box>
    </Box>
  );
}
